import { ChevronLeft, ChevronRight, Plus, Lock, Users, Globe, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGENDA, AGENDA_SOURCE_LEGEND } from "@/constants/testIds";
import { SOURCE_LABELS, STATUS_LABELS } from "@/lib/agendaConstants";

const SOURCE_LEGEND_ITEMS = [
  { icon: Users, label: SOURCE_LABELS.manual },
  { icon: Globe, label: SOURCE_LABELS.public_booking },
  { icon: Bot, label: SOURCE_LABELS.whatsapp_agent },
];

const VIEW_OPTIONS = [
  { key: "day", label: "Día", testId: AGENDA.viewDayButton },
  { key: "week", label: "Semana", testId: AGENDA.viewWeekButton },
  { key: "month", label: "Mes", testId: AGENDA.viewMonthButton },
];

/** Toolbar personalizado para reemplazar el toolbar default de react-big-calendar. */
export default function AgendaToolbar({
  label, view, onNavigate, onView,
  professionals, professionalFilter, onProfessionalFilterChange, showProfessionalFilter,
  sourceFilter, onSourceFilterChange, statusFilter, onStatusFilterChange,
  onNewAppointment, onNewBlock,
}) {
  return (
    <div className="flex flex-col gap-3 pb-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Fix AI (2026-10-23): en el celular lo que no entra pasa a la linea de abajo en vez de salirse de la pantalla */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onNavigate("TODAY")} data-testid={AGENDA.todayButton}>Hoy</Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onNavigate("PREV")} data-testid={AGENDA.prevButton}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onNavigate("NEXT")} data-testid={AGENDA.nextButton}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="font-serif text-xl sm:text-2xl text-foreground capitalize ml-1" data-testid={AGENDA.currentRangeLabel}>{label}</h2>
        </div>

        <div className="flex items-center gap-1 bg-[#F3F0EA] rounded-full p-1">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => onView(opt.key)}
              data-testid={opt.testId}
              className={`px-3.5 py-1.5 rounded-full text-sm font-sans transition-colors duration-200 ${
                view === opt.key ? "bg-brand text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        {showProfessionalFilter ? (
          <Select value={professionalFilter} onValueChange={onProfessionalFilterChange}>
            <SelectTrigger className="w-56" data-testid={AGENDA.professionalFilterSelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los profesionales</SelectItem>
              {professionals.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : <div />}

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 pl-1">Origen</span>
            <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
              <SelectTrigger className="w-44" data-testid={AGENDA.sourceFilterSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los orígenes</SelectItem>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 pl-1">Estado</span>
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="w-40" data-testid={AGENDA.statusFilterSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={onNewBlock} data-testid={AGENDA.newBlockButton}>
            <Lock className="w-3.5 h-3.5" /> Bloquear horario
          </Button>
          <Button size="sm" className="gap-1.5 bg-brand hover:bg-brand-hover text-white" onClick={onNewAppointment} data-testid={AGENDA.newAppointmentButton}>
            <Plus className="w-3.5 h-3.5" /> Nuevo turno
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground/80" data-testid={AGENDA_SOURCE_LEGEND}>
        <span className="uppercase tracking-wide text-[10px]">Origen del turno:</span>
        {SOURCE_LEGEND_ITEMS.map(({ icon: Icon, label }) => (
          <span key={label} className="flex items-center gap-1">
            <Icon className="w-3 h-3" /> {label}
          </span>
        ))}
      </div>
    </div>
  );
}
